import { ConfirmDialog, EmptyState, isolate } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  Delete20Regular,
  Document24Regular,
  FolderArrowRight20Regular,
  Send20Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SendTransmittalDialog } from '@/features/transmittals';
import { useNotify } from '@/shared/hooks/use-notify';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useRefreshFolders } from './api';
import { DocumentFiltersBar } from './document-filters';
import {
  DEFAULT_SORT,
  SEARCH_DELAY_MS,
  canDeleteDocument,
  canEditDocument,
  documentErrorMessage,
  everyAllows,
  type DocumentRow,
  type SortState,
} from './document-model';
import {
  useDeleteDocument,
  useDocumentPages,
  useRefreshDocuments,
  type DocumentFilters,
} from './documents-api';
import { DocumentsTable } from './documents-table';
import { MoveDocumentsDialog } from './move-documents-dialog';
import { isStaleVersion, isStepUpRequired } from './model';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"title":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fod5ikn","Bg96gwp":"faaz57k","Bhrd7zp":"fl43uef"},"selection":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f16xkysk"},"count":{"Bhrd7zp":"fl43uef","Bokbzmb":"f1axoyop"},"more":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f4d9j23","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"},"muted":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"names":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8zks9i","B1hrpyx":"f17mo7c8"}});

/** Names listed in the delete confirmation; the others are counted. */
const NAMES_SHOWN = 5;

type Dialog = 'move' | 'delete' | 'transmit' | null;

export interface DocumentsListProps {
  spaceId: string;
  /** The open folder. The list starts again (filters, sorting, selection) for another folder. */
  folderId: string;
  /** The caller may add files here: the empty state invites to drop some. */
  canAdd: boolean;
  /** The caller manages the space: "Send with a transmittal" is offered for a selection. */
  canTransmit?: boolean;
  /** A row was activated: the panel opens on this document. */
  onOpenDocument: (documentId: string) => void;
  /** These documents left the folder (moved or deleted): an open panel on one of them closes. */
  onGone: (documentIds: readonly string[]) => void;
}

/**
 * The documents of the open folder (GED-FE-03): search and filter chips, a sortable table read a
 * page at a time ("Show more"), and, for a selection, "Move to…" and "Delete" when every
 * selected document allows them. The server filters, sorts and pages (`nextCursor`).
 */
export function DocumentsList({
  spaceId,
  folderId,
  canAdd,
  canTransmit = false,
  onOpenDocument,
  onGone,
}: DocumentsListProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const refreshDocuments = useRefreshDocuments(spaceId);
  const refreshFolders = useRefreshFolders(spaceId);
  const remove = useDeleteDocument(spaceId);

  const [filters, setFilters] = useState<DocumentFilters>({});
  const [text, setText] = useState('');
  const query = useDebouncedValue(text.trim(), SEARCH_DELAY_MS);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog>(null);

  const pages = useDocumentPages(
    spaceId,
    folderId,
    { ...filters, ...(query ? { q: query } : {}) },
    sort,
  );
  const documents: readonly DocumentRow[] = pages.data?.pages.flatMap((page) => page.items) ?? [];
  const chosen = documents.filter((document) => selected.has(document.id));
  const filtered =
    filters.discipline !== undefined ||
    filters.phaseType !== undefined ||
    filters.docType !== undefined ||
    query !== '';

  const clearFilters = () => {
    setFilters({});
    setText('');
  };
  const toggle = (id: string, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const toggleAll = (on: boolean) => {
    setSelected(on ? new Set(documents.map((document) => document.id)) : new Set());
  };

  const labelOf = (document: DocumentRow): string =>
    document.revisionIndex
      ? t('delete.documentName', {
          title: isolate(document.title),
          index: isolate(document.revisionIndex),
        })
      : isolate(document.title);

  const deleteChosen = async () => {
    const gone: string[] = [];
    let firstError: unknown = null;
    for (const document of chosen) {
      try {
        await remove.mutateAsync({ documentId: document.id, version: document.version });
        gone.push(document.id);
      } catch (error) {
        if (isStepUpRequired(error)) throw error;
        firstError ??= error;
      }
    }
    if (gone.length > 0) {
      setSelected(new Set());
      onGone(gone);
      await Promise.all([refreshDocuments(), refreshFolders()]);
    }
    if (gone.length === chosen.length) {
      notify({ intent: 'success', message: t('delete.documentsDone', { count: gone.length }) });
    } else if (gone.length > 0) {
      notify({
        intent: 'warning',
        message: t('delete.documentsPartial', {
          deleted: gone.length,
          failed: chosen.length - gone.length,
          reason: documentErrorMessage(t, firstError),
        }),
      });
    } else {
      // Nothing was deleted: the dialog stays open and says why.
      throw firstError;
    }
  };

  const empty = filtered ? (
    <EmptyState
      headingLevel={4}
      illustration={<Document24Regular />}
      title={t('list.noMatch.title')}
      description={t('list.noMatch.description')}
      primaryAction={{ label: t('filters.clear'), onClick: clearFilters }}
    />
  ) : (
    <EmptyState
      headingLevel={4}
      illustration={<Document24Regular />}
      title={t('content.noDocuments.title')}
      description={
        canAdd ? t('content.noDocuments.description') : t('content.noDocuments.descriptionRead')
      }
    />
  );

  return (
    <section className={styles.root} aria-label={t('list.region')}>
      <h3 className={styles.title}>{t('list.title')}</h3>
      <DocumentFiltersBar
        filters={filters}
        onChange={(patch) => {
          setFilters((current) => ({ ...current, ...patch }));
        }}
        text={text}
        onTextChange={setText}
        onClear={clearFilters}
      />

      {chosen.length > 0 ? (
        <div className={styles.selection} role="group" aria-label={t('selection.label')}>
          <span className={styles.count} role="status">
            {t('selection.count', { count: chosen.length })}
          </span>
          {canTransmit ? (
            <Button
              size="small"
              icon={<Send20Regular />}
              onClick={() => {
                setDialog('transmit');
              }}
            >
              {t('transmittals:send.action')}
            </Button>
          ) : null}
          {everyAllows(chosen, canEditDocument) ? (
            <Button
              size="small"
              icon={<FolderArrowRight20Regular />}
              onClick={() => {
                setDialog('move');
              }}
            >
              {t('selection.move')}
            </Button>
          ) : null}
          {everyAllows(chosen, canDeleteDocument) ? (
            <Button
              size="small"
              icon={<Delete20Regular />}
              onClick={() => {
                setDialog('delete');
              }}
            >
              {t('selection.delete')}
            </Button>
          ) : null}
          <Button
            size="small"
            appearance="subtle"
            onClick={() => {
              setSelected(new Set());
            }}
          >
            {t('selection.clear')}
          </Button>
        </div>
      ) : null}

      {pages.isError && !pages.data ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('list.loadFailed')}</MessageBarBody>
          <MessageBarActions>
            <Button
              onClick={() => {
                void pages.refetch();
              }}
            >
              {t('common:actions.retry')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : (
        <DocumentsTable
          documents={documents}
          sort={sort}
          onSortChange={(next) => {
            setSort(next ?? DEFAULT_SORT);
          }}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onOpen={(document) => {
            onOpenDocument(document.id);
          }}
          loading={pages.isPending}
          empty={empty}
        />
      )}

      {pages.isError && pages.data ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('list.moreFailed')}</MessageBarBody>
        </MessageBar>
      ) : null}
      {documents.length > 0 ? (
        <div className={styles.more}>
          <p className={styles.muted} role="status">
            {t('list.shown', { count: documents.length })}
          </p>
          {pages.hasNextPage ? (
            <Button
              disabledFocusable={pages.isFetchingNextPage}
              onClick={() => {
                void pages.fetchNextPage();
              }}
            >
              {pages.isFetchingNextPage ? t('list.loadingMore') : t('list.loadMore')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {dialog === 'move' ? (
        <MoveDocumentsDialog
          spaceId={spaceId}
          documents={chosen}
          currentFolderId={folderId}
          onClose={() => {
            setDialog(null);
          }}
          onMoved={(ids) => {
            setSelected(new Set());
            onGone(ids);
          }}
        />
      ) : null}
      {dialog === 'transmit' ? (
        <SendTransmittalDialog
          spaceId={spaceId}
          documents={chosen}
          onClose={() => {
            setDialog(null);
          }}
          onSent={() => {
            setSelected(new Set());
          }}
        />
      ) : null}
      <ConfirmDialog
        open={dialog === 'delete'}
        destructive
        title={
          chosen.length === 1 && chosen[0]
            ? t('delete.documentTitle', { name: labelOf(chosen[0]) })
            : t('delete.documentsTitle', { count: chosen.length })
        }
        body={
          <>
            <p>{t('delete.documentsBody', { count: chosen.length })}</p>
            <ul className={styles.names}>
              {chosen.slice(0, NAMES_SHOWN).map((document) => (
                <li key={document.id}>
                  <bdi>{labelOf(document)}</bdi>
                </li>
              ))}
            </ul>
            {chosen.length > NAMES_SHOWN ? (
              <p>{t('delete.andOthers', { count: chosen.length - NAMES_SHOWN })}</p>
            ) : null}
          </>
        }
        confirmLabel={t('delete.confirm')}
        cancelLabel={t('common:actions.cancel')}
        onOpenChange={(next) => {
          if (!next) setDialog(null);
        }}
        onConfirm={deleteChosen}
        onError={(error) => {
          if (isStepUpRequired(error)) return;
          notify({
            intent: isStaleVersion(error) ? 'warning' : 'error',
            message: documentErrorMessage(t, error),
          });
          if (isStaleVersion(error)) {
            // Someone changed a document meanwhile: read the list again and start over.
            void refreshDocuments();
            setDialog(null);
          }
        }}
      />
    </section>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/documents-list.griffel.css";